import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../lib/cn';

const loaderVariants = cva(
  'flex items-center justify-center text-text-secondary',
  {
    variants: {
      variant: {
        fullscreen: 'min-h-screen bg-bg-primary',
        overlay: 'absolute inset-0 z-20 rounded-2xl bg-bg-primary/80',
        inline: 'relative',
      },
      size: {
        sm: 'gap-2 text-xs',
        md: 'gap-3 text-sm',
        lg: 'gap-4 text-base',
      },
    },
    defaultVariants: {
      variant: 'fullscreen',
      size: 'md',
    },
  }
);

const spinnerVariants = cva(
  'animate-spin rounded-full border-t-transparent',
  {
    variants: {
      variant: {
        fullscreen: 'border-4 border-border-primary',
        overlay: 'border-2 border-border-primary',
        inline: 'border-2 border-border-primary',
      },
      size: {
        sm: 'h-4 w-4',
        md: 'h-5 w-5',
        lg: 'h-12 w-12',
      },
    },
    defaultVariants: {
      variant: 'fullscreen',
      size: 'md',
    },
  }
);

export interface LoaderProps extends VariantProps<typeof loaderVariants> {
  message?: string;
  className?: string;
}

/**
 * Unified Loader component with variants
 * @param variant - Loader style variant (fullscreen, overlay, inline)
 * @param size - Loader size (sm, md, lg)
 * @param message - Loading message text
 */
export default function Loader({ 
  variant = 'fullscreen', 
  size = 'md', 
  message = 'Loading…',
  className 
}: LoaderProps) {
  const isFullscreen = variant === 'fullscreen';
  
  return (
    <div className={cn(loaderVariants({ variant, size }), className)}>
      {isFullscreen ? (
        <div className="flex flex-col items-center gap-3">
          <span 
            className={cn(spinnerVariants({ variant, size }))}
            aria-hidden="true"
          />
          <span className="text-sm font-medium">{message}</span>
        </div>
      ) : (
        <div className="flex items-center gap-3">
          <span 
            className={cn(spinnerVariants({ variant, size }))}
            aria-hidden="true"
          />
          <span>{message}</span>
        </div>
      )}
    </div>
  );
}
